import Alpine from 'alpinejs';
import MicroModal from 'micromodal';
import { createFrameListener, createStreamListener } from 'banzai/pendant/front/util/turbo.js';

declare global {
  // Assume Weglot has already been loaded and attached to window
  interface Window {
    Weglot: any;
    Turbo: any;
  }
}

function getWebSocketURL(relativePath: string) {
  const host = window.location.hostname;
  const port = window.location.port;
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${host}:${port}${relativePath}`;
}

Alpine.data('assistant', (webSocketUrl, getMessagesUrl: string) => ({
  webSocketUrl: getWebSocketURL(webSocketUrl),
  getMessagesUrl,
  socket: null,
  message: null,
  showEmptyView: true,
  showDisclaimer: false,
  waiting: false,

  init() {
    createStreamListener('pa-messages-container', 'update', () => setTimeout(() => this.onRenderMessages(), 100));
    createStreamListener('pa-messages-container', 'append', () => setTimeout(() => this.onRenderMessages(), 100));

    this.ensureSocket();

    // keepalive
    this.interval = setInterval(() => this.socket.send(JSON.stringify({ type: 'ping' })), 5000);

    setTimeout(() => {
      // If the autofocus attribute has been set on the input then give it the
      // focus. This is needed so that the focus is applied after the modal is
      // opened.
      const autofocus = this.$refs.messageInput.getAttribute('autofocus');
      if (autofocus !== null) {
        this.$refs.messageInput.focus();
      }
    }, 100);
  },

  destroy() {
    this.socket?.close();
  },

  async ensureSocket() {
    if (this.socket?.readyState == WebSocket.OPEN) {
      return;
    }

    return this.openSocket();
  },

  async openSocket() {
    return new Promise<void>((resolve, _reject) => {
      const params = this.sessionId ? new URLSearchParams({ 'session-id': this.sessionId }) : '';
      this.socket = new WebSocket(`${this.webSocketUrl}?${params}`);

      this.socket.addEventListener('open', (_event: Event) => {
        console.log('WebSocket connection opened.');
        resolve();
      });

      this.socket.addEventListener('message', async (event: MessageEvent) => {
        const {
          type,
          'message-id': messageId,
          'session-id': sessionId,
          'message-role': messageRole,
        } = JSON.parse(event.data);
        switch (type) {
          case 'init':
            this.sessionId = sessionId;
            break;
          case 'new-message':
            this.showEmptyView = false;
            this.showDisclaimer = false;
            await this.updateView(messageId);
            if (messageRole === 'assistant') {
              this.waiting = false;
              this.scrollToBottom();
            }
          case 'pong':
            // Don't nothing, silently
            break;
          case 'success':
            // Don't nothing, silently
            break;
          default:
            console.log(`Unknown message type: ${type}`);
        }
      });

      this.socket.addEventListener('close', (event: Event) => {
        console.log('WebSocket connection closed.');
      });
    });
  },

  async sendPrompt(event) {
    // If you use this in an Alpine on-click be sure and don't use the closing parenthesis
    // on the function so that the native event will getpassed to this function.
    const text = event?.target.textContent;
    const send = (p) => {
      this.message = p;
      this.sendMessage();
    };

    // If we can get the text from the target then assume its translated and send it to
    // the backend
    if (text?.length) {
      send(text);
      return;
    }

    const prompt = event.target.dataset?.prompt;

    // If we don't have a prompt then do nothing
    if (!prompt.length) {
      return;
    }

    send(await translatePrompt(prompt));
  },

  // Translate the prompt with Weglot. If we can't translate the promp or the translation
  // fails then return the original prompt.
  async translatePrompt(prompt: string) {
    if (!window.Weglot) {
      return prompt;
    }

    const currentLang = window.Weglot.getCurrentLang();

    // If we have prompt and the current language is the default language (en) then send
    // the prompt as is
    if (currentLang === 'en') {
      return prompt;
    }

    // The current language isn't english so try to translate the prompt via Weglot
    try {
      const r = await window.Weglot.translate({
        words: [{ w: prompt, t: 1 }],
        languageTo: currentLang,
      });

      return r.length ? r[0] : prompt;
    } catch (e) {
      console.log('Error translating the assistant prompt: ', e);
      return prompt;
    }
  },

  onRenderMessages() {
    if (!this.$refs.chatView) {
      // TODO: Understand why chatView is sometimes not defined
      return;
    }

    this.$refs.chatView.querySelectorAll('a').forEach((link) => {
      link.setAttribute('target', '_blank');
    });

    this.$nextTick(() => this.scrollToBottom());
  },

  scrollToBottom() {
    this.$refs.chatView.scrollTop = this.$refs.chatView.scrollHeight;
  },

  async sendMessage() {
    this.waiting = true;
    const r = await this.ensureSocket();

    this.socket.send(
      JSON.stringify({
        type: 'send-message',
        content: this.message,
        'session-id': this.sessionId,
      })
    );

    this.message = '';
  },

  async updateView(id?: string) {
    console.log('updateView: ', id);
    let url;
    if (!!id) {
      url = `${this.getMessagesUrl}/${id}`;
    } else {
      const params = new URLSearchParams({
        'session-id': this.sessionId,
      });
      url = `${this.getMessagesUrl}?${params}`;
    }
    const res = await fetch(url);
    const data = await res.text();
    Turbo.renderStreamMessage(data);
  },
}));
